import "./SchedulerClearances.scss";

import { Col, Row } from "react-bootstrap";
import {
  faAdd,
  faCalendarDay,
  faCircleCheck,
  faCircleInfo,
  faCircleXmark,
  faClock,
  faTrash,
  faTriangleExclamation,
  faUmbrellaBeach,
} from "@fortawesome/free-solid-svg-icons";
import { useEffect, useMemo, useState } from "react";

import ActionPillButton from "../../../commons/buttons/ActionPillButton";
import AppCard from "../../../commons/cards/AppCard";
import CompanyAPI from "../../company/CompanyAPI";
import ConfirmModal from "../../../commons/modal/ConfirmModal";
import EmptyState from "../../../commons/EmptyState";
import FloatingButton from "../../../commons/buttons/FloatingButton";
import FloatingVideoButton from "../../../commons/buttons/FloatingVideoButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import HallAppToast from "../../../commons/HallAppToast";
import MenuTitle from "../../../commons/menu/MenuTitle";
import PullToRefresh from "react-simple-pull-to-refresh";
import SchedulerClearancesModal from "./SchedulerClearancesModal";
import SchedulerConfigAPI from "../SchedulerConfigAPI";
import Skeleton from "../../../commons/skeleton/Skeleton";
import UserService from "../../../auth/UserService";
import { formatDate } from "../../../commons/util/HallAppUtil";

/* eslint-disable react-hooks/exhaustive-deps */
function SchedulerClearances() {
  const schedulerAPI = useMemo(() => new SchedulerConfigAPI(), []);
  const userAPI = useMemo(() => new UserService(), []);
  const companyAPI = useMemo(() => new CompanyAPI(), []);

  const [isLoading, setIsLoading] = useState(true);
  const [clearances, setClearances] = useState([]);

  const [showModal, setShowModal] = useState(false);

  const [showModalDelete, setShowModalDelete] = useState(false);
  const [isLoadingModalDelete, setIsLoadingModalDelete] = useState(false);
  const [currentClosed, setCurrentClosed] = useState(null);

  const getClearances = async () => {
    try {
      setIsLoading(true);
      const response = await schedulerAPI.getClearances(userAPI.getUserData().id);

      const list = Array.isArray(response?.data) ? response.data : [];

      const getDateTs = (x) => {
        const d1 = x?.closedDate ? new Date(x.closedDate).getTime() : NaN;
        if (!Number.isNaN(d1)) return d1;

        const d2 = x?.createDate ? new Date(x.createDate).getTime() : NaN;
        if (!Number.isNaN(d2)) return d2;

        return 0;
      };

      const statusRank = (s) => (s === "ACTIVE" ? 0 : 1);

      const ordered = [...list].sort((a, b) => {
        const sr = statusRank(a.status) - statusRank(b.status);
        if (sr !== 0) return sr;
        return getDateTs(b) - getDateTs(a);
      });

      setClearances(ordered);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getClearances();
  }, []);

  const getStatusLabel = (value) => {
    if (value === "ACTIVE") return "Ativo";
    if (value === "INACTIVE") return "Cancelado";
    return "--";
  };

  const getStatusTone = (value) => {
    if (value === "ACTIVE") return "success";
    if (value === "INACTIVE") return "danger";
    return "muted";
  };

  const openDelete = (value) => {
    setCurrentClosed(value);
    setShowModalDelete(true);
  };

  const remove = async () => {
    if (!currentClosed?.id) return;

    try {
      setIsLoadingModalDelete(true);
      await schedulerAPI.removeClosedScheduler(
        currentClosed.id,
        companyAPI.getCompanyManagment().id
      );
      await getClearances();
      HallAppToast.success();
      setShowModalDelete(false);
      setCurrentClosed(null);
    } catch (_) {
      HallAppToast.genericError();
    } finally {
      setIsLoadingModalDelete(false);
    }
  };

  const renderBadge = (status) => {
    const tone = getStatusTone(status);
    const isActive = status === "ACTIVE";

    return (
      <span className={`sc-badge sc-badge--${tone}`} title={getStatusLabel(status)}>
        <FontAwesomeIcon icon={isActive ? faCircleCheck : faCircleXmark} />
        <span>{getStatusLabel(status)}</span>
      </span>
    );
  };

  const renderValidity = (value) => {
    const ok = value?.isValidDate !== false;

    return (
      <span className={`sc-valid sc-valid--${ok ? "ok" : "warn"}`}>
        <FontAwesomeIcon icon={ok ? faCircleCheck : faTriangleExclamation} />
        <span>{ok ? "Válido" : "Data inválida"}</span>
      </span>
    );
  };

  const timeText = (v) => {
    const s = v?.startTime || "--";
    const e = v?.endTime || "--";
    if (s === "--" && e === "--") return "Dia todo";
    return `${s} · ${e}`;
  };

  const hasRange = (v) => {
    if (!v?.endClosedDate) return false;
    return String(v.endClosedDate) !== String(v.closedDate);
  };

  const dateText = (v) => {
    if (hasRange(v)) return `${formatDate(v.closedDate)} até ${formatDate(v.endClosedDate)}`;
    return formatDate(v.closedDate);
  };

  const deleteDateText = (v) => {
    if (!v) return "";
    if (hasRange(v)) return `${formatDate(v.closedDate)} até ${formatDate(v.endClosedDate)}`;
    return formatDate(v.closedDate);
  };

  const SkeletonCard = () => (
    <div className="sc-skeletonCard" aria-hidden="true">
      <div className="sc-skeletonCard__top">
        <div className="sc-skeletonCard__date">
          <Skeleton variant="circle" width={34} height={34} />
          <div className="sc-skeletonCard__dateText">
            <Skeleton width={70} height={10} />
            <Skeleton width={140} height={14} style={{ marginTop: 6 }} />
          </div>
        </div>
        <div className="sc-skeletonCard__tags">
          <Skeleton width={92} height={22} style={{ borderRadius: 999 }} />
          <Skeleton width={92} height={22} style={{ borderRadius: 999 }} />
        </div>
      </div>

      <div className="sc-skeletonCard__body">
        <div className="sc-skeletonRow">
          <Skeleton variant="circle" width={30} height={30} />
          <div className="sc-skeletonRow__text">
            <Skeleton width={70} height={10} />
            <Skeleton width={"85%"} height={14} style={{ marginTop: 6 }} />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="container sc-page">
      <PullToRefresh onRefresh={getClearances}>
        <MenuTitle title="Feriados e Folgas"/>
        <hr />

        <Row className="g-3">
          {isLoading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <Col xs={12} sm={12} md={6} lg={4} key={`sk-${i}`}>
                <AppCard>
                  <SkeletonCard />
                </AppCard>
              </Col>
            ))
          ) : clearances.length === 0 ? (
            <EmptyState
              title="Sem feriados ou folgas cadastrados"
              description="Cadastre feriados e folgas para bloquear horários no calendário e evitar novos agendamentos nessas datas."
              icon={<FontAwesomeIcon icon={faUmbrellaBeach} />}
              actionLabel="Cadastrar feriado/folga"
              onAction={() => setShowModal(true)}
            />
          ) : (
            clearances.map((value) => {
              const muted = value?.isValidDate === false;

              return (
                <Col xs={12} sm={12} md={6} lg={4} key={value.id}>
                  <AppCard backgroundColor={muted ? "#F3F4F6" : ""}>
                    <div className={`sc-card ${muted ? "sc-card--muted" : ""}`}>
                      <div className="sc-card__top">
                        <div className="sc-card__date">
                          <span className="sc-icon">
                            <FontAwesomeIcon icon={faCalendarDay} />
                          </span>

                          <div className="sc-card__dateText">
                            <div className="sc-card__label">{hasRange(value) ? "Período" : "Data"}</div>
                            <div className="sc-card__value">{dateText(value)}</div>
                          </div>
                        </div>

                        <div className="sc-card__right">
                          <div className="sc-card__tags">
                            {renderBadge(value.status)}
                            {renderValidity(value)}
                          </div>

                          <div style={{marginTop: '10px'}}>
                            <ActionPillButton
                              icon={faTrash}
                              label="Excluir"
                              onClick={() => openDelete(value)}
                              color="#b42318"
                              borderColor="#ffd6d6"
                              title="Excluir clube"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="sc-card__body">
                        <div className="sc-row">
                          <div className="sc-row__left">
                            <span className="sc-icon sc-icon--soft">
                              <FontAwesomeIcon icon={faClock} />
                            </span>
                            <div>
                              <div className="sc-card__label">Horário</div>
                              <div className="sc-card__value">{timeText(value)}</div>
                            </div>
                          </div>
                        </div>

                        {!!value?.reason && (
                          <div className="sc-row sc-row--reason">
                            <div className="sc-row__left">
                              <span className="sc-icon sc-icon--soft">
                                <FontAwesomeIcon icon={faCircleInfo} />
                              </span>
                              <div>
                                <div className="sc-card__label">Motivo</div>
                                <div className="sc-card__value">{value.reason}</div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </AppCard>
                </Col>
              );
            })
          )}
        </Row>

        <div style={{ marginTop: 30 }} />

        <FloatingButton icon={faAdd} onClick={() => setShowModal(true)} />

        {showModalDelete && (
          <ConfirmModal
            show={showModalDelete}
            title="Remover"
            isLoading={isLoadingModalDelete}
            confirm={remove}
            closeModal={() => setShowModalDelete(false)}
          >
            Deseja remover o registro{" "}
            <span style={{ fontWeight: "bold" }}>
              {deleteDateText(currentClosed)}
              <br />
              {currentClosed?.startTime || "-"} / {currentClosed?.endTime || "-"}
            </span>
            ?
          </ConfirmModal>
        )}

        {showModal && (
          <SchedulerClearancesModal
            show={showModal}
            fetchAll={getClearances}
            closeShow={() => setShowModal(false)}
          />
        )}
      </PullToRefresh>

      <FloatingVideoButton youtubeUrl={"https://www.youtube.com/embed/mWuvgk41En4"} />
      <div style={{ height: "calc(50px + 72px)" }}></div>
    </div>
  );
}

export default SchedulerClearances;
